// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE FIELD — the physical farm underneath all the money.
//
// Every other view on this site is denominated in dollars. This one is
// denominated in hectares, tonnes and head, because the survey also measures
// what is actually in the paddock, and the physical account turns out to carry
// the plainest version of the site's argument: broadacre farms have been
// cropping a steadily larger share of the ground they hold, and the yield they
// take off it has not moved anything like as far.
//
// Three questions, three forms:
//   1. WHAT IT GROWS   crop × year grid of sown area, per region.
//   2. WHAT IT YIELDS  small multiples of tonnes per hectare, per crop.
//   3. HOW MUCH OF IT  all 32 regions ranked by the share of the farm under
//                      crop, first surveyed year against latest settled year.
//
// NO DESIGN SPEC WAS SUPPLIED (the brief arrived with the spec field literally
// `undefined`), so the composition is my own. Two constraints shaped it: the
// sibling views already own the dollar accounts (costgap owns receipts against
// costs and the cost breakdown; engines, mechanism and trails own the return
// series), and nothing else on the site touches the physical variables at all.
//
// WHY THIS VIEW LOADS THE CUBE. core.json carries the crop RECEIPTS but no
// sown areas, no production and no livestock numbers, and it carries a sampling
// error only for the rate-of-return series. Tiering a wheat yield with the rate
// of return's error would be inventing evidence, so this view pays for
// cube.json (cached for the session, and shared with the cost view) and tiers
// every mark from that variable's own published RSE.
//
// WHY EVERYTHING HERE IS TEAL. The palette gives teal to what the FARMING
// earned and ochre to what the LAND appreciated. Nothing on this page is a
// return at all — it is the farming's physical side, end to end — so one hue
// carries the whole view and ochre appears nowhere. Using ochre for hectares
// would imply these charts were about the land's price, which is the one thing
// they are not.

import { loadCore, loadCube } from '../data';
import type { Core, Cube, Region, RegionYear } from '../types';
import { FARMING, GRAPHITE, NEUTRAL, PAPER, SCOPE_SHORT, ZONE_ORDER, mixHex } from '../theme';
import {
  TIER_WORD, collapse, evidenceLines, isGap, isOutlineOnly, isRankable,
  isTokenSized, tierOpacity, tierPrefix, type Tier,
} from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartSvg, el, extent, finite, hatchDefs,
  linearScale, markTip,
} from '../components/svg';
import { esc, fy, ha, money, num, pct } from '../format';
import { currentRoute, setParams } from '../router';
import { closeButton, openOverlay } from '../overlay';
import { glossIcon, glossTerm } from '../components/glossary';

/* ── the cube variables this view reads ──────────────────────────────────── */

const V_OPERATED = 'Area operated at 30 June (ha)';
const V_CROPPED = 'Total area cropped (ha)';
const V_SHEEP = 'Sheep flock at 30 June (no.)';
const V_BEEF = 'Beef herd at 30 June (no.)';
const V_WOOL = 'Wool produced (kg)';
const V_RECEIPTS = 'Total cash receipts ($)';

/**
 * The crops, and a trap worth naming.
 *
 * ABARES publishes PARENT lines alongside the individual crops: "Oilseeds"
 * contains canola, and "Grain legumes" contains lupins and field peas. Listing
 * parents beside their children double-counts the paddock — the eight leaf
 * crops below sum to at most the published total area cropped in 1,076 of the
 * 1,080 region-years that have one (the four exceptions are rounding, under
 * 1%), whereas adding the two parents pushes the sum 27% over the total in the
 * Western Australian wheat belt alone. So only leaves are drawn here, and the
 * remainder — hay, pasture seed and minor crops — is left unclaimed rather than
 * invented as a residual row.
 */
interface Crop { label: string; area: string; produced: string | null; receipts: string | null }
const CROPS: Crop[] = [
  { label: 'Wheat', area: 'Wheat area sown (ha)', produced: 'Wheat produced (t)', receipts: 'Wheat receipts ($)' },
  { label: 'Barley', area: 'Barley area sown (ha)', produced: 'Barley produced (t)', receipts: 'Barley receipts ($)' },
  { label: 'Canola', area: 'Canola area sown (ha)', produced: 'Canola produced (t)', receipts: 'Canola receipts ($)' },
  { label: 'Oats', area: 'Oats area sown (ha)', produced: 'Oats produced (t)', receipts: 'Oats receipts ($)' },
  { label: 'Lupins', area: 'Lupins area sown (ha)', produced: 'Lupins produced (t)', receipts: 'Lupins receipts ($)' },
  { label: 'Field peas', area: 'Field peas area sown (ha)', produced: 'Field peas produced (t)', receipts: 'Field peas receipts ($)' },
  { label: 'Sorghum', area: 'Sorghum area sown (ha)', produced: 'Sorghum produced (t)', receipts: 'Sorghum receipts ($)' },
  // Rice has a sown area in this survey but no published production line, so it
  // appears in the grid and never in the yield panels.
  { label: 'Rice', area: 'Rice area sown (ha)', produced: null, receipts: null },
];

/**
 * core.json carries an evidence tier on every region-year; src/types.ts still
 * describes the older `rel` shape. Rather than edit a shared file from inside
 * one view, the extra fields are declared here and the cast is done once.
 */
type TieredYear = RegionYear & { tier: Tier; apprTier: Tier };
type TieredRegion = Omit<Region, 'series'> & {
  tier: Tier; weakYears: number; worstYearTier: Tier; series: TieredYear[];
};

interface Cell { value: number | null; rse: number | null }
type CellFn = (variable: string, region: string, year: number) => Cell;
interface Reading extends Cell { tier: Tier }

interface Ctx {
  core: Core;
  cell: CellFn;
  region: TieredRegion;
  regions: TieredRegion[];
  uid: string;
  /** The last SETTLED year. 2024-25 is provisional and never ends a comparison. */
  lastSettled: number;
  sampleFor: (region: string, year: number) => number | null;
  scopeTo: (name: string) => void;
}

type Mode = 'share' | 'hectares';

/* ── the evidence ladder, mirrored from pipeline/parse.mjs ───────────────── */

// Identical to `tierOf` in the pipeline: the worse of the two ladders wins,
// because the failure modes are independent. A MISSING error is tier 4, never
// tier 1 — defaulting an unmeasured cell to zero error would silently promote
// every unmeasured cell to full confidence.
const RSE_BANDS = { GOOD: 25, CAUTION: 50, POOR: 100 };
const SAMPLE_BANDS = { GOOD: 30, FAIR: 15, POOR: 5 };

function rseTier(rse: number | null): Tier {
  if (rse == null || !Number.isFinite(rse)) return 4;
  const r = Math.abs(rse);
  if (r <= RSE_BANDS.GOOD) return 1;
  if (r <= RSE_BANDS.CAUTION) return 2;
  if (r <= RSE_BANDS.POOR) return 3;
  return 4;
}

function sampleTier(sample: number | null | undefined): Tier {
  if (sample == null || !Number.isFinite(sample)) return 4;
  if (sample >= SAMPLE_BANDS.GOOD) return 1;
  if (sample >= SAMPLE_BANDS.FAIR) return 2;
  if (sample >= SAMPLE_BANDS.POOR) return 3;
  return 4;
}

function tierOf(value: number | null, rse: number | null, sample: number | null | undefined): Tier {
  if (value == null || !Number.isFinite(value)) return 5;
  return Math.max(rseTier(rse), sampleTier(sample)) as Tier;
}

const worst = (a: Tier, b: Tier): Tier => Math.max(a, b) as Tier;

/* ── cube access ─────────────────────────────────────────────────────────── */

/**
 * cube.json is indexed [variable][region][year]. The `Cube` interface types it
 * only as a three-deep array and lists its name arrays in the other order, so
 * every index here is resolved by NAME and never by position: a silent
 * transposition would return plausible, wrong numbers rather than an error.
 */
function cubeLookup(cube: Cube): CellFn {
  const vi = new Map(cube.variables.map((v, i) => [v, i]));
  const ri = new Map(cube.regions.map((v, i) => [v, i]));
  const yi = new Map(cube.years.map((v, i) => [v, i]));
  return (variable, region, year) => {
    const a = vi.get(variable); const b = ri.get(region); const c = yi.get(year);
    if (a == null || b == null || c == null) return { value: null, rse: null };
    return { value: cube.values[a]?.[b]?.[c] ?? null, rse: cube.rse[a]?.[b]?.[c] ?? null };
  };
}

/* ── small helpers ───────────────────────────────────────────────────────── */

let seq = 0;

function div(cls: string, html?: string): HTMLDivElement {
  const d = document.createElement('div');
  d.className = cls;
  if (html != null) d.innerHTML = html;
  return d;
}

function h2(text: string, cls = 'card-title'): HTMLHeadingElement {
  const h = document.createElement('h2');
  h.className = cls;
  h.textContent = text;
  return h;
}

/**
 * A chart in its own horizontal scroller.
 *
 * `svg.chart` carries `max-width: 100%`, which at 375px would squash a
 * 36-column grid into unreadable 8px columns instead of letting it scroll.
 * `min-width` beats `max-width` in the cascade, so setting it here restores
 * scroll-inside-the-box without touching the shared stylesheet — and the page
 * itself still never scrolls sideways.
 */
function frame(svg: SVGSVGElement, minWidth: number): HTMLDivElement {
  const f = div('chart-frame');
  const scroll = div('chart-scroll');
  svg.style.minWidth = `${minWidth}px`;
  scroll.append(svg);
  f.append(scroll);
  return f;
}

/** Make an SVG mark a real, keyboard-operable control. */
function activate(
  node: SVGElement,
  lines: (string | null | undefined | false)[],
  ariaLabel: string,
  onGo: () => void,
): void {
  markTip(node, lines);
  node.setAttribute('role', 'button');
  node.setAttribute('tabindex', '0');
  // markTip sets aria-label from the tip text; a control also needs its ACTION named.
  node.setAttribute('aria-label', `${ariaLabel}. ${lines.filter(Boolean).join('. ')}`);
  node.style.cursor = 'pointer';
  node.addEventListener('click', onGo);
  node.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onGo();
    }
  });
}

/** A hover-only mark: tooltip, no focus, no click, and no cursor affordance. */
function hoverOnly(node: SVGElement, lines: (string | null | undefined | false)[]): void {
  markTip(node, lines);
}

// WHY THE GRID CELLS AND THE YIELD BANDS ARE HOVER-ONLY, with no click.
//
// Between them they are more than five hundred marks. Making each one clickable
// would put five hundred controls on the page that no keyboard could reach, and
// making each one focusable instead would put five hundred stops in the tab
// order — both are worse than the third option, which is that a data cell is
// simply a data cell. Every ACTION on this page lives on a real control with a
// real focus ring: the year strip along the top of the grid opens a season, the
// crop name at the left of each row opens a crop, and each yield panel carries
// its own button. No mark on this page shows a pointer cursor without responding
// to it, and nothing here is reachable by mouse alone.

/** Visible focus wash for SVG controls, wired inside the chart so it dies with it. */
function focusWash(svg: SVGSVGElement): void {
  svg.addEventListener('focusin', (e) => {
    const t = e.target as SVGElement;
    if (t.hasAttribute?.('data-wash')) t.setAttribute('fill-opacity', '0.16');
  });
  svg.addEventListener('focusout', (e) => {
    const t = e.target as SVGElement;
    if (t.hasAttribute?.('data-wash')) t.setAttribute('fill-opacity', '0');
  });
}

const tonnes = (v: number | null): string =>
  v == null || !Number.isFinite(v) ? '—' : `${num(v)} t`;

const head = (v: number | null): string =>
  v == null || !Number.isFinite(v) ? '—' : `${num(v)} head`;

/* ── the view ────────────────────────────────────────────────────────────── */

export async function renderField(host: HTMLElement): Promise<void> {
  const [core, cube] = await Promise.all([loadCore(), loadCube()]);
  const regions = core.regions as unknown as TieredRegion[];
  const cell = cubeLookup(cube);
  const lastSettled = core.settledYears[core.settledYears.length - 1];

  // Sample size is per region-year and it is half of every tier on this page.
  const samples = new Map<string, number | null>();
  for (const r of regions) for (const s of r.series) samples.set(`${r.name}|${s.year}`, s.sample);
  const sampleFor = (region: string, year: number) => samples.get(`${region}|${year}`) ?? null;

  const route = currentRoute();
  let region = regions.find((r) => r.name === route.params.get('region')) ?? defaultRegion(regions);
  let mode: Mode = route.params.get('mode') === 'hectares' ? 'hectares' : 'share';

  host.innerHTML = '';

  const headEl = div('view-head');
  headEl.innerHTML = `
    <h1>The Field</h1>
    <p class="lede">Hectares, tonnes and head — the physical farm underneath every dollar
      figure on this site. What the ${glossTerm('broadacre', 'broadacre')} paddock grows, what it yields,
      and how much of the property is under crop at all.</p>
    <p class="small muted">Every figure is an average ${glossTerm('perFarm', 'per surveyed farm')}
      and comes straight from the survey — nothing here is recalculated except tonnes per
      hectare, which is stated as a ratio of two published lines and tiered on the worse of
      the two. ${glossIcon('aagis')}</p>`;

  const controls = div('controls');

  const regionControl = div('control');
  const label = document.createElement('label');
  label.className = 'field-label';
  label.setAttribute('for', 'field-region');
  label.textContent = 'Region';
  const select = document.createElement('select');
  select.id = 'field-region';
  for (const zone of ZONE_ORDER) {
    const group = document.createElement('optgroup');
    group.label = `${zone} zone`;
    for (const r of regions.filter((x) => x.zone === zone)) {
      const opt = document.createElement('option');
      opt.value = r.name;
      opt.textContent = r.name;
      group.append(opt);
    }
    if (group.childElementCount) select.append(group);
  }
  select.value = region.name;
  regionControl.append(label, select);

  const modeControl = div('control');
  const modeLabel = document.createElement('span');
  modeLabel.className = 'field-label';
  modeLabel.id = 'field-mode-label';
  modeLabel.textContent = 'Crop grid shows';
  const seg = div('seg');
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-labelledby', 'field-mode-label');
  const segButtons: Array<[Mode, string]> = [['share', 'Share of the cropped area'], ['hectares', 'Hectares per farm']];
  for (const [key, text] of segButtons) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = text;
    b.dataset.mode = key;
    b.addEventListener('click', () => {
      if (mode === key) return;
      mode = key;
      setParams({ mode: key === 'share' ? null : key });
      draw();
    });
    seg.append(b);
  }
  modeControl.append(modeLabel, seg);

  const pills = div('row');
  pills.style.flexWrap = 'wrap';
  controls.append(regionControl, modeControl, pills);

  const body = div('stack');
  const foot = div('stack');
  host.append(headEl, controls, body, foot);

  const draw = () => {
    const ctx: Ctx = {
      core, cell, region, regions, lastSettled, sampleFor,
      uid: `fld${++seq}`,
      scopeTo: (name: string) => {
        const next = regions.find((r) => r.name === name);
        if (!next) return;
        region = next;
        select.value = name;
        setParams({ region: name });
        draw();
        body.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    };
    for (const b of seg.querySelectorAll('button')) {
      b.setAttribute('aria-pressed', String(b.dataset.mode === mode));
    }
    pills.innerHTML = `
      <span class="pill">${esc(region.zone)} zone</span>
      <span class="pill">${region.state ? esc(region.state) : 'multi-state'}</span>
      <span class="pill">median sample ${region.medianSample == null ? '—' : Math.round(region.medianSample)} farms</span>
      <span class="pill">surveyed from ${esc(fy(region.firstYear))}</span>`;
    body.innerHTML = '';
    body.append(buildGlance(ctx), buildGrid(ctx, mode), buildYields(ctx), buildIntensity(ctx));
  };

  select.addEventListener('change', () => {
    const next = regions.find((r) => r.name === select.value);
    if (!next) return;
    region = next;
    // Scoping is shareable: the URL carries the region, so a link into this
    // view lands on the same paddock rather than on the default one.
    setParams({ region: region.name });
    draw();
  });

  draw();

  foot.append(div('note', `
    <strong>What this page is not.</strong> There is no dollar return anywhere on it. The
    survey's headline finding — that ${glossTerm('capitalAppreciation', 'capital appreciation')}
    supplied roughly three quarters of the total return to farm capital over these 36 years —
    is settled elsewhere on the site. What the paddock shows is the other half of the story:
    the physical business that produced the thin farming return, and how hard it has been
    worked in the meantime.`));
  foot.append(div('note', `
    <strong>Reading the evidence.</strong> Every mark is tiered on its own published sampling
    error and its own sample size. Firm and fair marks are solid; a weak mark is an outline,
    because only its direction is usable; a "no basis" mark is drawn at a fixed token size so
    its magnitude is never encoded; and where ABARES published nothing, the line breaks and
    the cell is blank — never zero, never interpolated.
    ${glossTerm('provisional', 'Provisional')} ${esc(fy(core.provisionalYear ?? 2025))} is hatched
    and is excluded from every mean, ordering and comparison on this page. ${glossIcon('rse')}`));

  const scope = document.createElement('p');
  scope.className = 'xsmall muted';
  scope.textContent = SCOPE_SHORT;
  foot.append(scope);
}

function defaultRegion(regions: TieredRegion[]): TieredRegion {
  // Open on the best-evidenced region rather than the alphabetically first one:
  // a reader's first impression of a sample survey should not be a wall of grey.
  const firm = regions.filter((r) => r.tier === 1);
  const pool = firm.length ? firm : regions;
  return [...pool].sort((a, b) => (b.medianSample ?? 0) - (a.medianSample ?? 0))[0];
}

/* ── the farm at a glance ────────────────────────────────────────────────── */

function buildGlance(ctx: Ctx): HTMLElement {
  const { region, cell, lastSettled, sampleFor } = ctx;
  const sample = sampleFor(region.name, lastSettled);

  const at = (variable: string, year = lastSettled): Reading => {
    const c = cell(variable, region.name, year);
    return { ...c, tier: tierOf(c.value, c.rse, sampleFor(region.name, year)) };
  };

  const operated = at(V_OPERATED);
  const cropped = at(V_CROPPED);
  const sheep = at(V_SHEEP);
  const beef = at(V_BEEF);
  const wool = at(V_WOOL);

  const share = cropped.value != null && operated.value ? (cropped.value / operated.value) * 100 : null;
  const shareTier = worst(cropped.tier, operated.tier);

  const stat = (value: string, labelText: string, note: string, klass = '') => `
    <div class="stat">
      <div class="stat-value ${klass}">${esc(value)}</div>
      <div class="stat-label">${labelText}</div>
      <div class="stat-note">${note}</div>
    </div>`;

  const ev = (t: Tier) => `${esc(TIER_WORD[t])}${t >= 3 ? ' — read the direction only' : ''}`;

  const row = div('stat-row');
  row.innerHTML = [
    stat(`${tierPrefix(operated.tier)}${operated.value == null ? '—' : ha(operated.value)}`,
      'Area operated', ev(operated.tier)),
    stat(`${tierPrefix(cropped.tier)}${cropped.value == null ? '—' : ha(cropped.value)}`,
      'Total area cropped', ev(cropped.tier), 'farming'),
    stat(share == null ? '—' : `${tierPrefix(shareTier)}${pct(share, 0)}`,
      'Of the farm, under crop', ev(shareTier), 'farming'),
    stat(`${tierPrefix(sheep.tier)}${head(sheep.value)}`, 'Sheep flock at 30 June', ev(sheep.tier)),
    stat(`${tierPrefix(beef.tier)}${head(beef.value)}`, 'Beef herd at 30 June', ev(beef.tier)),
    stat(wool.value == null ? '—' : `${tierPrefix(wool.tier)}${num(wool.value)} kg`,
      'Wool produced', ev(wool.tier)),
  ].join('');

  const card = div('card stack');
  card.append(h2(`${region.name}: the farm at a glance`));
  card.append(div('card-sub', `The latest settled year, ${esc(fy(lastSettled))}, averaged over the
    ${sample == null ? 'surveyed' : `${sample}`} farms surveyed here that year. ${glossIcon('perFarm')}`));
  card.append(row);
  card.append(div('note', `
    <strong>Head and hectares are not a headcount.</strong> These are per-farm averages from a
    sample, not a census of the region, and a region with fewer, larger farms shows bigger
    per-farm numbers without necessarily running more stock in total. Where the survey could
    not support a figure the tile reads "—" rather than a zero.`));
  return card;
}

/* ── chart 1: what the paddock grows ─────────────────────────────────────── */

interface GridCell {
  year: number; area: number | null; rse: number | null; tier: Tier;
  share: number | null; cropped: number | null;
}

function buildGrid(ctx: Ctx, mode: Mode): HTMLElement {
  const { core, region, cell, uid, lastSettled, sampleFor } = ctx;
  const years = core.years;
  const prov = core.provisionalYear;

  const croppedAt = new Map(years.map((y) => [y, cell(V_CROPPED, region.name, y).value]));
  const operatedAt = new Map(years.map((y) => [y, cell(V_OPERATED, region.name, y).value]));

  const rows = CROPS.map((crop) => {
    const cells: GridCell[] = years.map((year) => {
      const c = cell(crop.area, region.name, year);
      const tot = croppedAt.get(year) ?? null;
      return {
        year,
        area: c.value,
        rse: c.rse,
        tier: tierOf(c.value, c.rse, sampleFor(region.name, year)),
        cropped: tot,
        share: c.value != null && tot != null && tot > 0 ? (c.value / tot) * 100 : null,
      };
    });
    const last = cells.find((c) => c.year === lastSettled);
    const ordered = !!last && isRankable(last.tier) && (last.area ?? 0) > 0;
    return { crop, cells, sortKey: ordered ? (last!.area ?? 0) : -1, ordered };
  }).filter((r) => r.cells.some((c) => (c.area ?? 0) > 0));

  const unordered = rows.filter((r) => !r.ordered).length;
  // Ordering rows by size is a ranking, so it takes firm-or-fair evidence only;
  // everything else keeps ABARES' own order at the bottom, and the count is printed.
  rows.sort((a, b) => b.sortKey - a.sortKey);

  const card = div('card stack');
  card.append(h2('What the paddock grows'));
  card.append(div('card-sub', mode === 'share'
    ? `One row per crop, one column per year, ${esc(fy(years[0]))} on the left to
       ${esc(fy(years[years.length - 1]))} on the right. The darker the cell, the bigger that
       crop's share of the ground the farm put under crop that year. Read a row across to see
       a rotation change; read a column down to see what a single season looked like.`
    : `One row per crop, one column per year. The darker the cell, the more hectares of that
       crop were sown on the average surveyed farm — so the grid darkens overall as farms got
       bigger, and a row that darkens faster than its neighbours is a crop taking ground.`));

  if (!rows.length) {
    card.append(div('empty-state', `No crop is sown on the surveyed farms in
      ${esc(region.name)} in any year of this survey — it is grazing country.`));
    return card;
  }

  const labelW = 132;
  const cellW = 20;
  const cellH = 22;
  const headerH = 16;
  const topPad = 62;
  const w = labelW + cellW * years.length + 16;
  const h = topPad + rows.length * cellH + 26;

  const svg = chartSvg(w, h, mode === 'share'
    ? `Each crop's sown area as a share of the total area cropped in ${region.name}, ${fy(years[0])} to ${fy(years[years.length - 1])}`
    : `Hectares sown of each crop per surveyed farm in ${region.name}, ${fy(years[0])} to ${fy(years[years.length - 1])}`);
  svg.append(hatchDefs(`${uid}-gridprov`, GRAPHITE, 0.5));
  focusWash(svg);

  const cx = (i: number) => labelW + i * cellW;
  const gridTop = topPad;
  const gridBottom = topPad + rows.length * cellH;

  // The colour ramp is set by firm-and-fair cells only. Weak cells are drawn as
  // outlines and "no basis" cells as tokens, so neither paints a fill — letting
  // one of them stretch the scale would dim every trustworthy value on it.
  const value = (c: GridCell) => (mode === 'share' ? c.share : c.area);
  const scaleMax = Math.max(
    mode === 'share' ? 5 : 1,
    ...rows.flatMap((r) => r.cells
      .filter((c) => isRankable(c.tier) && value(c) != null)
      .map((c) => value(c) as number)),
  );

  // Three NT regions enter the survey in 1991-92. Those columns are not "no
  // estimate published" — there was no survey at all — so they are shaded and
  // labelled as such rather than left to read like a withheld figure.
  if (region.firstYear > years[0]) {
    const n = years.filter((v) => v < region.firstYear).length;
    const wash = el('rect', {
      x: cx(0), y: gridTop, width: cellW * n, height: gridBottom - gridTop,
      fill: NEUTRAL, opacity: 0.1, 'pointer-events': 'all',
    });
    hoverOnly(wash, [
      `Not surveyed — ${region.name} enters the ABARES survey in ${fy(region.firstYear)}`,
      'Blank because there was no survey here, not because a figure was withheld.',
    ]);
    svg.append(wash);
  }

  if (prov != null && years.indexOf(prov) >= 0) {
    const i = years.indexOf(prov);
    svg.append(el('rect', {
      x: cx(i), y: gridTop, width: cellW, height: gridBottom - gridTop,
      fill: `url(#${uid}-gridprov)`, opacity: 0.65, 'pointer-events': 'none',
    }));
    const t = el('text', { class: 'axis-text', x: cx(i) + cellW / 2, y: topPad - headerH - 22, 'text-anchor': 'middle' });
    t.textContent = 'prov.';
    svg.append(t);
  }

  for (let i = 0; i < years.length; i++) {
    const yr = years[i];
    if (yr % 5 !== 0 && i !== years.length - 1) continue;
    const t = el('text', { class: 'axis-text', x: cx(i) + cellW / 2, y: topPad - headerH - 8, 'text-anchor': 'middle' });
    t.textContent = fy(yr);
    svg.append(t);
  }

  // A slim focusable strip of year headers: one tab stop per year, and the only
  // control in this chart that opens a season. It is drawn as visible structure
  // — a shaded band with a rule under it — so it reads as something to press
  // rather than as an invisible hot zone.
  svg.append(el('rect', {
    x: cx(0), y: topPad - headerH, width: cellW * years.length, height: headerH,
    fill: GRAPHITE, opacity: 0.06,
  }));
  svg.append(el('line', {
    class: 'axis-line', x1: cx(0), y1: topPad, x2: cx(years.length), y2: topPad,
  }));
  const stripLabel = el('text', {
    class: 'axis-text', x: labelW - 10, y: topPad - 4, 'text-anchor': 'end',
  });
  stripLabel.textContent = 'season →';
  svg.append(stripLabel);

  for (let i = 0; i < years.length; i++) {
    const yr = years[i];
    const cropped = croppedAt.get(yr) ?? null;
    const operated = operatedAt.get(yr) ?? null;
    const band = el('rect', {
      x: cx(i), y: topPad - headerH, width: cellW, height: headerH,
      fill: GRAPHITE, 'fill-opacity': 0, 'pointer-events': 'all',
    });
    band.setAttribute('data-wash', '');
    activate(band, [
      `Total area cropped ${cropped == null ? 'not published' : ha(cropped)}`,
      operated != null && `Area operated ${ha(operated)}`,
      cropped != null && operated ? `${pct((cropped / operated) * 100, 0)} of the farm under crop` : null,
      `${fy(yr)}${yr === prov ? ' — provisional, ABARES will revise it' : ''}`,
      ...evidenceLines(sampleFor(region.name, yr), tierOf(cropped, cell(V_CROPPED, region.name, yr).rse, sampleFor(region.name, yr))),
      'Select for the whole season',
    ], `${region.name}, ${fy(yr)}`, () => openSeason(ctx, yr));
    svg.append(band);
  }

  rows.forEach((row, ri) => {
    const yTop = topPad + ri * cellH;

    const last = row.cells.find((c) => c.year === lastSettled);
    const labelNode = el('text', {
      class: 'axis-text', x: labelW - 10, y: yTop + cellH / 2 + 4, 'text-anchor': 'end',
      fill: 'currentColor',
    });
    labelNode.textContent = row.crop.label;
    activate(labelNode, [
      row.ordered && last
        ? `${row.crop.label} ${ha(last.area)} sown in ${fy(lastSettled)}${last.share != null ? `, ${pct(last.share, 0)} of the cropped area` : ''}`
        : `${row.crop.label}: no firm or fair estimate in ${fy(lastSettled)}`,
      `${region.name} · sown area, tonnes and yield year by year`,
      'Select to open this crop',
    ], `${row.crop.label} in ${region.name}`, () => openCrop(ctx, row.crop));
    svg.append(labelNode);

    row.cells.forEach((c, i) => {
      if (c.year < region.firstYear) return;
      // Tier 5 / no published figure: nothing is drawn. No zero fill, no
      // interpolation, and a blank is never read back as "none was sown".
      if (isGap(c.tier)) return;
      const v = value(c);
      if (v == null) return;
      const x0 = cx(i);

      let node: SVGElement;
      if (isTokenSized(c.tier)) {
        // Fixed token: a tier-4 cell shows that something was measured and
        // never how much. Magnitude is deliberately not encoded.
        node = el('rect', {
          x: x0 + cellW / 2 - 3.5, y: yTop + cellH / 2 - 3.5, width: 7, height: 7,
          rx: 1.5, fill: collapse(FARMING, 4), opacity: tierOpacity(4),
        });
      } else if (isOutlineOnly(c.tier)) {
        node = el('rect', {
          x: x0 + 1.5, y: yTop + 1.5, width: cellW - 3, height: cellH - 3, rx: 2,
          fill: 'none', stroke: collapse(FARMING, 3), 'stroke-width': 1.4, opacity: tierOpacity(3),
        });
      } else {
        const k = Math.max(0, Math.min(1, v / scaleMax));
        node = el('rect', {
          x: x0 + 1, y: yTop + 1, width: cellW - 2, height: cellH - 2, rx: 2,
          fill: collapse(mixHex(PAPER, FARMING, k), c.tier), opacity: tierOpacity(c.tier),
        });
      }
      hoverOnly(node, [
        `${row.crop.label} ${tierPrefix(c.tier)}${ha(c.area)} sown`,
        // The share inherits the cell's tier — it is built from the same number,
        // so it gets the same tilde rather than looking firmer than its input.
        c.share != null && `${tierPrefix(c.tier)}${pct(c.share, 1)} of the ${ha(c.cropped)} cropped`,
        `${fy(c.year)}${c.year === prov ? ' (provisional)' : ''} · ${region.name}`,
        ...evidenceLines(sampleFor(region.name, c.year), c.tier, c.rse),
      ]);
      svg.append(node);
    });
  });

  card.append(frame(svg, w));

  const legend = div('legend');
  const unit = mode === 'share' ? '' : ' ha';
  legend.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${mixHex(PAPER, FARMING, 0.1)};border:1px solid var(--border-default)"></span>little or none</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${mixHex(PAPER, FARMING, 0.5)}"></span>about ${mode === 'share' ? pct(scaleMax / 2, 0) : `${num(scaleMax / 2)}${unit}`}</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${FARMING}"></span>${mode === 'share' ? pct(scaleMax, 0) : `${num(scaleMax)}${unit}`} or more</span>
    <span class="legend-item"><span class="legend-swatch" style="border:1.5px solid ${collapse(FARMING, 3)};background:transparent"></span>outline: weak — direction only</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${collapse(FARMING, 4)};transform:scale(.55)"></span>token: no basis for a figure</span>
    <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1px dashed var(--border-strong)"></span>blank: nothing published</span>`;
  card.append(legend);

  card.append(div('note', `
    <strong>These rows are not a pie.</strong> Only the individual crops are drawn.
    ABARES also publishes two PARENT lines — "oilseeds", which contains canola, and "grain
    legumes", which contains lupins and field peas — and putting a parent next to its own
    children would count the same paddock twice. The rows here therefore add to slightly less
    than the total area cropped: the rest is hay, pasture seed and minor crops, which the
    survey does not publish as separate lines.
    ${unordered > 0
      ? `Rows are ordered by size in ${esc(fy(lastSettled))}; ${unordered} of ${rows.length}
         could not be ordered that way — weak or unpublished that year — and sit last.`
      : `Rows are ordered by size in ${esc(fy(lastSettled))}; none had to be excluded from that ordering.`}
    Hover any cell for its figures; select a crop name for that crop year by year, or a year
    in the strip along the top for everything the survey measured that season.`));
  return card;
}

/* ── chart 2: what the paddock yields ────────────────────────────────────── */

interface YieldPoint {
  year: number; tPerHa: number | null; tier: Tier;
  produced: number | null; area: number | null; rseA: number | null; rseP: number | null;
}

function buildYields(ctx: Ctx): HTMLElement {
  const { core, region, cell, sampleFor } = ctx;
  const years = core.years;
  const prov = core.provisionalYear;
  const settled = new Set(core.settledYears);

  const panels = CROPS
    .filter((crop) => crop.produced != null)
    .map((crop) => {
      const points: YieldPoint[] = years.map((year) => {
        const a = cell(crop.area, region.name, year);
        const p = cell(crop.produced as string, region.name, year);
        const sample = sampleFor(region.name, year);
        const tierA = tierOf(a.value, a.rse, sample);
        const tierP = tierOf(p.value, p.rse, sample);
        const usable = a.value != null && a.value > 0 && p.value != null;
        return {
          year,
          // The ratio's own sampling error is NOT published. The honest floor is
          // the worse of the two inputs' tiers — a yield cannot be better
          // evidenced than the weaker of the numbers it is built from.
          tPerHa: usable ? (p.value as number) / (a.value as number) : null,
          tier: usable ? worst(tierA, tierP) : 5,
          produced: p.value, area: a.value, rseA: a.rse, rseP: p.rse,
        };
      });
      const usable = points.filter((q) => q.tPerHa != null);
      // A mean takes firm-or-fair evidence only, and never the provisional year.
      const rankable = points.filter((q) => isRankable(q.tier) && q.tPerHa != null && settled.has(q.year));
      const settledSurveyed = points.filter((q) => settled.has(q.year) && q.year >= region.firstYear).length;
      const mean = rankable.length >= 5
        ? rankable.reduce((s, q) => s + (q.tPerHa as number), 0) / rankable.length
        : null;
      return { crop, points, usableCount: usable.length, rankable: rankable.length, settledSurveyed, mean };
    })
    .filter((p) => p.usableCount >= 6);

  const card = div('card stack');
  card.append(h2('What the paddock yields'));
  card.append(div('card-sub', `
    One small chart per crop, all on the same time axis: tonnes harvested per hectare sown, on
    the average surveyed farm in ${esc(region.name)}. Each panel starts at zero and keeps its
    own vertical scale, because a barley yield and a canola yield are different quantities and
    a shared axis would flatten one of them into a straight line. The deep troughs are drought
    years; they are real, and they are exactly why the average matters less than the range.`));

  if (!panels.length) {
    card.append(div('empty-state', `The survey publishes no crop with both a sown area and a
      production figure in enough years for ${esc(region.name)}, so no yield is drawn. Nothing
      is estimated in its place.`));
    return card;
  }

  const grid = div('grid');
  for (const panel of panels) {
    const w = 340; const h = 168;
    const M = { l: 42, r: 12, t: 10, b: 38 };
    const svg = chartSvg(w, h,
      `${panel.crop.label} yield in tonnes per hectare, ${region.name}, ${fy(years[0])} to ${fy(years[years.length - 1])}`);
    focusWash(svg);
    const top = extent(panel.points.map((p) => p.tPerHa))[1];
    const x = linearScale([years[0], years[years.length - 1]], [M.l, w - M.r]);
    const y = linearScale([0, top * 1.12 || 1], [h - M.b, M.t]);

    axisLeft(svg, y, M.l, { format: (v) => v.toFixed(1), gridTo: w - M.r, ticks: y.ticks(4) });
    axisBottom(svg, x, h - M.b, {
      ticks: years.filter((v) => v % 10 === 0),
      format: (v) => `'${String(v).slice(2)}`,
      x0: M.l, x1: w - M.r,
    });

    // Settled years only in the drawn line; the provisional year gets its own
    // dashed stub, and a year with no published figure BREAKS the line rather
    // than being bridged across.
    const settledPts = panel.points.filter((p) => settled.has(p.year))
      .map((p) => ({ x: x(p.year), y: p.tPerHa == null ? null : y(p.tPerHa) }));
    for (const d of brokenPath(settledPts)) {
      svg.append(el('path', { d, fill: 'none', stroke: FARMING, 'stroke-width': 1.9, 'stroke-linejoin': 'round' }));
    }
    if (prov != null) {
      const a = panel.points.find((p) => p.year === prov - 1);
      const b = panel.points.find((p) => p.year === prov);
      if (a?.tPerHa != null && b?.tPerHa != null) {
        svg.append(el('path', {
          d: `M${finite(x(a.year)).toFixed(2)},${finite(y(a.tPerHa)).toFixed(2)} L${finite(x(b.year)).toFixed(2)},${finite(y(b.tPerHa)).toFixed(2)}`,
          fill: 'none', stroke: FARMING, 'stroke-width': 1.9, 'stroke-dasharray': '4 3',
        }));
      }
    }

    if (panel.mean != null) {
      const my = finite(y(panel.mean));
      const meanLine = el('line', {
        x1: M.l, y1: my, x2: w - M.r, y2: my,
        stroke: GRAPHITE, 'stroke-width': 1, 'stroke-dasharray': '2 3', opacity: 0.75,
        'pointer-events': 'none',
      });
      markTip(meanLine, [
        `${panel.crop.label} mean ${panel.mean.toFixed(2)} t/ha`,
        `over ${panel.rankable} firm or fair settled years — the provisional year is excluded`,
      ]);
      svg.append(meanLine);
    }

    const marks = el('g', { 'pointer-events': 'none' });
    for (const p of panel.points) {
      if (p.tPerHa == null || isGap(p.tier)) continue;
      const shade = collapse(FARMING, p.tier);
      const dot = el('circle', {
        cx: finite(x(p.year)), cy: finite(y(p.tPerHa)), r: 2.6,
        fill: isOutlineOnly(p.tier) ? 'none' : (p.year === prov ? PAPER : shade),
        stroke: shade,
        'stroke-width': isOutlineOnly(p.tier) || isTokenSized(p.tier) ? 1.4 : 0.7,
        'stroke-dasharray': isTokenSized(p.tier) ? '2 2' : null,
        opacity: tierOpacity(p.tier),
      });
      // The marker carries its own accessible name even though the hit band
      // over it is what a pointer actually finds.
      markTip(dot, [
        `${panel.crop.label} ${tierPrefix(p.tier)}${p.tPerHa.toFixed(2)} t/ha`,
        `${fy(p.year)}${p.year === prov ? ' (provisional)' : ''}`,
        ...evidenceLines(sampleFor(region.name, p.year), p.tier),
      ]);
      marks.append(dot);
    }

    const bands = el('g');
    const step = (w - M.r - M.l) / years.length;
    for (const p of panel.points) {
      const band = el('rect', {
        x: finite(x(p.year)) - step / 2, y: M.t, width: step, height: h - M.b - M.t,
        fill: GRAPHITE, 'fill-opacity': 0, 'pointer-events': 'all',
      });
      hoverOnly(band, [
        p.tPerHa == null
          ? `${panel.crop.label}: no yield can be formed for ${fy(p.year)}`
          : `${panel.crop.label} ${tierPrefix(p.tier)}${p.tPerHa.toFixed(2)} t/ha`,
        p.produced != null && `${tonnes(p.produced)} off ${ha(p.area)} sown`,
        `${fy(p.year)}${p.year === prov ? ' (provisional)' : ''} · ${region.name}`,
        ...evidenceLines(sampleFor(region.name, p.year), p.tier),
        p.rseA != null && `±${Math.round(Math.abs(p.rseA))}% error on the sown area`,
        p.rseP != null && `±${Math.round(Math.abs(p.rseP))}% error on the tonnes`,
      ]);
      bands.append(band);
    }
    svg.append(bands, marks);

    const panelCard = div('card stack');
    panelCard.style.padding = 'var(--space-md)';
    // The keyboard route into this crop is a real <button>, not an invisible
    // rectangle: a heading that opens the detail is discoverable by every input.
    const title = document.createElement('h3');
    const titleBtn = document.createElement('button');
    titleBtn.type = 'button';
    titleBtn.className = 'btn btn-sm';
    titleBtn.textContent = `See ${panel.crop.label} year by year`;
    titleBtn.addEventListener('click', () => openCrop(ctx, panel.crop));
    title.className = 'card-title';
    title.textContent = panel.crop.label;
    panelCard.append(title);
    panelCard.append(div('card-sub', panel.mean != null
      ? `mean ${panel.mean.toFixed(2)} t/ha over ${panel.rankable} firm or fair settled years
         (${Math.max(0, panel.settledSurveyed - panel.rankable)} of this region's settled years excluded)`
      : 'no mean is printed — too few firm or fair years to average one honestly'));
    panelCard.append(frame(svg, 280));
    panelCard.append(titleBtn);
    grid.append(panelCard);
  }
  card.append(grid);

  const legend = div('legend');
  legend.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${FARMING}"></span>tonnes per hectare sown</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${GRAPHITE};height:2px"></span>the crop's mean over firm and fair settled years</span>
    <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1.5px solid ${collapse(FARMING, 3)};border-radius:50%"></span>hollow: weak — direction only</span>
    <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1px dashed ${GRAPHITE}"></span>dashed: provisional ${esc(fy(prov ?? 2025))}, or a break where nothing was published</span>`;
  card.append(legend);

  card.append(div('note', `
    <strong>A yield here is a ratio of two survey estimates.</strong> ABARES publishes tonnes
    produced and hectares sown, each with its own sampling error, but publishes no error for
    the ratio — so every yield mark is tiered on the WORSE of its two inputs, which is a floor
    rather than a measurement. The mean line is taken over firm and fair years only and never
    includes the ${glossTerm('provisional', 'provisional')} year; the count it left out is
    printed under each crop. A crop with fewer than five such years gets no mean at all.`));
  return card;
}

/* ── chart 3: how much of the farm is under crop ─────────────────────────── */

function buildIntensity(ctx: Ctx): HTMLElement {
  const { core, regions, region, cell, lastSettled, sampleFor, scopeTo } = ctx;

  interface RowD {
    name: string; zone: string; then: number; now: number;
    tierThen: Tier; tierNow: Tier; startYear: number;
    croppedNow: number | null; operatedNow: number | null;
  }

  const settled = core.settledYears;

  /** The tier of a share is the worse of the two published lines it is built from. */
  const shareAt = (name: string, year: number) => {
    const c = cell(V_CROPPED, name, year);
    const o = cell(V_OPERATED, name, year);
    const s = sampleFor(name, year);
    return {
      cropped: c.value,
      operated: o.value,
      share: c.value != null && o.value ? (c.value / o.value) * 100 : null,
      tier: worst(tierOf(c.value, c.rse, s), tierOf(o.value, o.rse, s)),
    };
  };

  const rows: RowD[] = [];
  let excluded = 0;
  for (const r of regions) {
    const now = shareAt(r.name, lastSettled);
    // A "then and now" pair is a comparison, so BOTH ends must be firm or fair.
    // The end is fixed at the latest settled year: if the current estimate is
    // weak there is nothing here worth comparing to, and the region drops out.
    if (!isRankable(now.tier) || now.share == null) { excluded++; continue; }
    // The start FLOATS to the earliest settled year the survey can actually
    // measure, never earlier than the region's own first year — three NT
    // regions enter in 1991-92, and an assumed 1990 start would compare against
    // a blank. Each row prints the year it starts from, because a comparison
    // whose span is hidden is a comparison a reader cannot check.
    let start: { year: number; share: number; tier: Tier } | null = null;
    for (const y of settled) {
      if (y < r.firstYear || y >= lastSettled) continue;
      const s = shareAt(r.name, y);
      if (isRankable(s.tier) && s.share != null) { start = { year: y, share: s.share, tier: s.tier }; break; }
    }
    if (!start) { excluded++; continue; }
    rows.push({
      name: r.name, zone: r.zone, startYear: start.year,
      then: start.share, now: now.share,
      tierThen: start.tier, tierNow: now.tier,
      croppedNow: now.cropped, operatedNow: now.operated,
    });
  }
  rows.sort((a, b) => b.now - a.now);

  const card = div('card stack');
  card.append(h2('How much of the farm is under crop'));
  card.append(div('card-sub', `
    Every survey region the evidence allows, on one axis: the share of the area operated that
    was sown to a crop, then (hollow) against ${esc(fy(lastSettled))} (solid). The length of the
    bar is the change, and the year each bar starts from is printed beside the region, because
    a few regions cannot be measured this way until a year or two later than the rest. Select
    any region to move this whole page to it.`));

  if (!rows.length) {
    card.append(div('empty-state',
      'No region has firm or fair estimates of both cropped and operated area at both ends of the comparison.'));
    return card;
  }

  const labelW = 292;
  const yearColW = 80;
  const valueW = 96;
  const rowH = 23;
  const M = { t: 26, b: 50 };
  const w = 860;
  const h = M.t + rows.length * rowH + M.b;
  const plotL = labelW;
  const plotR = w - valueW;
  const maxV = Math.max(...rows.map((r) => Math.max(r.then, r.now)));
  const x = linearScale([0, Math.min(100, Math.ceil(maxV / 10) * 10) || 10], [plotL, plotR]);

  const svg = chartSvg(w, h,
    `Share of the area operated that is sown to crop, in each of the 32 survey regions, first surveyed year compared with ${fy(lastSettled)}`);
  focusWash(svg);

  for (const t of x.ticks(6)) {
    svg.append(el('line', {
      class: 'grid-line', x1: finite(x(t)), y1: M.t - 6, x2: finite(x(t)), y2: h - M.b,
    }));
  }
  axisBottom(svg, x, h - M.b, {
    format: (v) => `${v}%`, title: 'share of the area operated that is cropped',
    x0: plotL, x1: plotR,
  });

  rows.forEach((r, i) => {
    const yc = M.t + i * rowH + rowH / 2;
    const tier = worst(r.tierThen, r.tierNow);
    const shade = collapse(FARMING, tier);
    const isCurrent = r.name === region.name;

    const hit = el('rect', {
      x: 0, y: M.t + i * rowH, width: w, height: rowH,
      fill: FARMING, 'fill-opacity': isCurrent ? 0.09 : 0, 'pointer-events': 'all',
    });
    if (!isCurrent) hit.setAttribute('data-wash', '');
    activate(hit, [
      `${r.name} ${pct(r.then, 1)} → ${pct(r.now, 1)} of the farm under crop`,
      r.croppedNow != null && r.operatedNow != null
        ? `${ha(r.croppedNow)} cropped of ${ha(r.operatedNow)} operated in ${fy(lastSettled)}`
        : null,
      `${fy(r.startYear)} → ${fy(lastSettled)} · ${r.zone} zone`,
      ...evidenceLines(sampleFor(r.name, lastSettled), tier),
      isCurrent ? 'This is the region shown above' : 'Select to move this page to this region',
    ], `${r.name}`, () => scopeTo(r.name));
    svg.append(hit);

    const label = el('text', {
      class: 'axis-text', x: labelW - yearColW - 10, y: yc + 4, 'text-anchor': 'end',
      fill: 'currentColor', 'pointer-events': 'none',
      'font-weight': isCurrent ? '700' : null,
    });
    label.textContent = r.name.length > 33 ? `${r.name.slice(0, 32)}…` : r.name;
    svg.append(label);

    const from = el('text', {
      class: 'axis-text', x: labelW - 12, y: yc + 4, 'text-anchor': 'end',
      'pointer-events': 'none',
    });
    from.textContent = `from ${fy(r.startYear)}`;
    svg.append(from);

    const xa = finite(x(r.then));
    const xb = finite(x(r.now));
    svg.append(el('line', {
      x1: xa, y1: yc, x2: xb, y2: yc, stroke: shade, 'stroke-width': 2.6,
      'stroke-linecap': 'round', opacity: tierOpacity(tier), 'pointer-events': 'none',
    }));
    const dotThen = el('circle', {
      cx: xa, cy: yc, r: 4.4, fill: PAPER, stroke: collapse(FARMING, r.tierThen),
      'stroke-width': 1.8, opacity: tierOpacity(r.tierThen), 'pointer-events': 'none',
    });
    hoverOnly(dotThen, [
      `${r.name} ${tierPrefix(r.tierThen)}${pct(r.then, 1)} of the farm under crop`,
      `${fy(r.startYear)} — the earliest year this region can be measured firmly`,
      ...evidenceLines(sampleFor(r.name, r.startYear), r.tierThen),
    ]);
    const dotNow = el('circle', {
      cx: xb, cy: yc, r: 4.8, fill: collapse(FARMING, r.tierNow),
      opacity: tierOpacity(r.tierNow), 'pointer-events': 'none',
    });
    hoverOnly(dotNow, [
      `${r.name} ${tierPrefix(r.tierNow)}${pct(r.now, 1)} of the farm under crop`,
      `${fy(lastSettled)} — the latest settled year`,
      ...evidenceLines(sampleFor(r.name, lastSettled), r.tierNow),
    ]);
    svg.append(dotThen, dotNow);

    const val = el('text', {
      class: 'axis-text', x: plotR + 12, y: yc + 4, 'text-anchor': 'start',
      fill: 'currentColor', 'pointer-events': 'none',
    });
    val.textContent = `${r.then.toFixed(0)} → ${r.now.toFixed(0)}%`;
    svg.append(val);
  });

  card.append(frame(svg, w));

  const legend = div('legend');
  legend.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${PAPER};border:2px solid ${FARMING};border-radius:50%"></span>the earliest year the region can be measured firmly</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${FARMING};border-radius:50%"></span>${esc(fy(lastSettled))}, the latest settled year</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${collapse(FARMING, 2)}"></span>paler bars carry weaker evidence</span>`;
  card.append(legend);

  const risers = rows.filter((r) => r.now > r.then).length;
  card.append(div('small muted', `
    ${risers} of the ${rows.length} regions drawn here crop a larger share of the farm than they
    did at the start of their span, and ${rows.length - risers} crop a smaller share.
    ${excluded > 0
      ? `${excluded} of the ${regions.length} regions are not drawn at all. In almost every case
         the reason is the same: the cropped area in ${esc(fy(lastSettled))} is itself a weak
         estimate, because these are pastoral regions where next to nothing is sown and a survey
         cannot measure "next to nothing" precisely. Their absence is not evidence that they
         crop a lot — it is the survey declining to put a number on very little.`
      : 'No region had to be excluded from this comparison.'}`));
  return card;
}

/* ── drill-downs ─────────────────────────────────────────────────────────── */

/** Everything the survey measured in the paddock in one season. */
function openSeason(ctx: Ctx, year: number): void {
  const { core, region, cell, sampleFor } = ctx;
  const sample = sampleFor(region.name, year);
  const prov = year === core.provisionalYear;

  const at = (variable: string): Reading => {
    const c = cell(variable, region.name, year);
    return { ...c, tier: tierOf(c.value, c.rse, sample) };
  };

  const operated = at(V_OPERATED);
  const cropped = at(V_CROPPED);
  const receipts = at(V_RECEIPTS);

  const cropRows = CROPS.map((crop) => {
    const a = at(crop.area);
    const p = crop.produced ? at(crop.produced) : null;
    const rec = crop.receipts ? at(crop.receipts) : null;
    const yieldV = a.value && p?.value != null ? p.value / a.value : null;
    return { crop, a, p, rec, yieldV };
  }).filter((r) => (r.a.value ?? 0) > 0);

  const evidence = (r: Reading) =>
    `${esc(TIER_WORD[r.tier])}${r.rse != null ? ` · ±${Math.round(Math.abs(r.rse))}%` : ''}`;

  openOverlay({
    panelClass: 'modal',
    label: `${region.name}, ${fy(year)}`,
    lockScroll: true,
    build(panel, close) {
      const headEl = div('overlay-head');
      const h = document.createElement('h2');
      h.textContent = `${region.name} — ${fy(year)}`;
      headEl.append(h, closeButton(close, 'Close this season'));

      const bodyEl = div('stack');
      bodyEl.innerHTML = `
        <p class="small muted">${sample == null ? 'Sample size not published' : `${sample} surveyed farms behind this season`}
          · ${esc(region.zone)} zone${prov ? ' · <strong>provisional — ABARES will revise these figures</strong>' : ''}</p>
        <div class="table-scroll"><table><thead><tr>
            <th>The ground</th><th class="num">Per farm</th><th>Evidence</th>
          </tr></thead><tbody>
            <tr><td>Area operated</td><td class="num">${esc(tierPrefix(operated.tier))}${esc(operated.value == null ? '—' : ha(operated.value))}</td><td class="small muted">${evidence(operated)}</td></tr>
            <tr><td>Total area cropped</td><td class="num">${esc(tierPrefix(cropped.tier))}${esc(cropped.value == null ? '—' : ha(cropped.value))}</td><td class="small muted">${evidence(cropped)}</td></tr>
            <tr><td>Share of the farm under crop</td><td class="num">${cropped.value != null && operated.value ? esc(pct((cropped.value / operated.value) * 100, 1)) : '—'}</td><td class="small muted">${esc(TIER_WORD[worst(cropped.tier, operated.tier)])}</td></tr>
            <tr><td>Sheep flock at 30 June</td><td class="num">${esc(tierPrefix(at(V_SHEEP).tier))}${esc(head(at(V_SHEEP).value))}</td><td class="small muted">${evidence(at(V_SHEEP))}</td></tr>
            <tr><td>Beef herd at 30 June</td><td class="num">${esc(tierPrefix(at(V_BEEF).tier))}${esc(head(at(V_BEEF).value))}</td><td class="small muted">${evidence(at(V_BEEF))}</td></tr>
            <tr><td>Wool produced</td><td class="num">${esc(tierPrefix(at(V_WOOL).tier))}${at(V_WOOL).value == null ? '—' : `${esc(num(at(V_WOOL).value))} kg`}</td><td class="small muted">${evidence(at(V_WOOL))}</td></tr>
            <tr><td>Total cash receipts</td><td class="num">${esc(tierPrefix(receipts.tier))}${esc(money(receipts.value, { long: true }))}</td><td class="small muted">${evidence(receipts)}</td></tr>
        </tbody></table></div>
        ${cropRows.length ? `
          <h3 class="card-title">The crops sown that season</h3>
          <div class="table-scroll"><table><thead><tr>
              <th>Crop</th><th class="num">Sown</th><th class="num">Of the cropped area</th>
              <th class="num">Produced</th><th class="num">t/ha</th><th class="num">Receipts</th><th>Evidence</th>
            </tr></thead><tbody>
            ${cropRows.map((r) => `
              <tr>
                <td>${esc(r.crop.label)}</td>
                <td class="num">${esc(tierPrefix(r.a.tier))}${esc(r.a.value == null ? '—' : ha(r.a.value))}</td>
                <td class="num">${r.a.value != null && cropped.value ? esc(pct((r.a.value / cropped.value) * 100, 1)) : '—'}</td>
                <td class="num">${esc(r.p ? tonnes(r.p.value) : '—')}</td>
                <td class="num">${r.yieldV == null ? '—' : esc(r.yieldV.toFixed(2))}</td>
                <td class="num">${esc(r.rec ? money(r.rec.value, { long: true }) : '—')}</td>
                <td class="small muted">${evidence(r.a)}</td>
              </tr>`).join('')}
          </tbody></table></div>
          <p class="xsmall muted">Sown areas are the individual crops only; "oilseeds" and "grain
            legumes" are parent lines that would double-count canola, lupins and field peas, so
            they are left out and the crops shown add to less than the total area cropped.</p>`
          : '<p class="small muted">No crop was sown on the surveyed farms here this season.</p>'}`;

      panel.append(headEl, bodyEl);
    },
  });
}

/** One crop, year by year in this region: area, tonnes, yield, receipts. */
function openCrop(ctx: Ctx, crop: Crop): void {
  const { core, region, cell, sampleFor } = ctx;

  const rows = core.years
    .filter((year) => year >= region.firstYear)
    .map((year) => {
      const sample = sampleFor(region.name, year);
      const a = cell(crop.area, region.name, year);
      const p = crop.produced ? cell(crop.produced, region.name, year) : { value: null, rse: null };
      const rec = crop.receipts ? cell(crop.receipts, region.name, year) : { value: null, rse: null };
      const cropped = cell(V_CROPPED, region.name, year).value;
      const tierA = tierOf(a.value, a.rse, sample);
      const tierP = tierOf(p.value, p.rse, sample);
      return {
        year, sample, a, p, rec, cropped, tierA, tierP,
        share: a.value != null && cropped ? (a.value / cropped) * 100 : null,
        yieldV: a.value && p.value != null ? p.value / a.value : null,
        yieldTier: a.value && p.value != null ? worst(tierA, tierP) : (5 as Tier),
      };
    });

  const blanks = rows.filter((r) => isGap(r.tierA)).length;

  openOverlay({
    panelClass: 'modal',
    label: `${crop.label} in ${region.name}`,
    lockScroll: true,
    build(panel, close) {
      const headEl = div('overlay-head');
      const h = document.createElement('h2');
      h.textContent = `${crop.label} — ${region.name}`;
      headEl.append(h, closeButton(close, `Close ${crop.label}`));

      const bodyEl = div('stack');
      bodyEl.innerHTML = `
        <p class="small muted">Per surveyed farm, ${esc(fy(region.firstYear))} to
          ${esc(fy(core.years[core.years.length - 1]))}. Sown area and tonnes are published
          lines; tonnes per hectare is the ratio of the two and carries the worse of their
          two evidence tiers.</p>
        <div class="table-scroll"><table><thead><tr>
            <th>Year</th><th class="num">Sown</th><th class="num">Of the cropped area</th>
            <th class="num">Produced</th><th class="num">t/ha</th><th class="num">Receipts</th>
            <th class="num">Sample</th><th>Evidence</th>
          </tr></thead><tbody>
          ${rows.map((r) => `
            <tr>
              <td class="nowrap">${esc(fy(r.year))}${r.year === core.provisionalYear ? ' <span class="pill">prov.</span>' : ''}</td>
              <td class="num">${esc(tierPrefix(r.tierA))}${esc(r.a.value == null ? '—' : ha(r.a.value))}</td>
              <td class="num">${r.share == null ? '—' : esc(pct(r.share, 1))}</td>
              <td class="num">${esc(tonnes(r.p.value))}</td>
              <td class="num">${r.yieldV == null ? '—' : `${esc(tierPrefix(r.yieldTier))}${esc(r.yieldV.toFixed(2))}`}</td>
              <td class="num">${esc(r.rec.value == null ? '—' : money(r.rec.value, { long: true }))}</td>
              <td class="num">${r.sample == null ? '—' : r.sample}</td>
              <td class="small muted">${esc(TIER_WORD[r.tierA])}${r.a.rse != null ? ` · ±${Math.round(Math.abs(r.a.rse))}%` : ''}</td>
            </tr>`).join('')}
        </tbody></table></div>
        <p class="small muted">${blanks > 0
          ? `${blanks} of these ${rows.length} surveyed years carry no published sown area for ${esc(crop.label)}. They are blank here and they break the line on the chart — a blank is not a zero, and nothing is interpolated across one.`
          : `Every surveyed year here carries a published sown area for ${esc(crop.label)}.`}</p>
        <p class="xsmall muted">${esc(SCOPE_SHORT)}</p>`;

      panel.append(headEl, bodyEl);
    },
  });
}
